import { CapType, FilterType as FilterOperator } from "types/enums";

import {
    createAffiliateRule,
    createCapRule,
    createFieldRule,
    createGroup,
    createTimeRule,
    createWeekdayRule,
} from "./filterEditorFactory";
import { countRules } from "./filterEditorReducer";
import { FilterEditorRoot, FilterEntry, FilterGroup, FilterItem, FilterRule } from "../types/filterEditor";
import { FilterLogicalOperator, FilterNode } from "../types/filterTree";

export const OWNER_ID_TAG = "owner_id";
export const FORM_FIELD_TAG_PREFIX = "data_";
/** `{time}` = seconds since midnight, `{weekday}` = ISO day 1 (Monday) .. 7 (Sunday). Both come from BE `defaultData()`. */
export const TIME_TAG = "time";
export const WEEKDAY_TAG = "weekday";
export const WEEKDAYS: readonly number[] = [1, 2, 3, 4, 5, 6, 7];
/** 24h `H:mm`, the only time format BE converts to seconds (MR !1520 makes it strict). */
export const TIME_PATTERN = /^(?:[01]?\d|2[0-3]):[0-5]\d$/;

/** Operators without a right-hand value. BE ignores `right` for them. */
export const VALUE_LESS_OPERATORS: readonly FilterOperator[] = [
    FilterOperator.IsTrue,
    FilterOperator.IsFalse,
    FilterOperator.IsEmpty,
    FilterOperator.IsNotEmpty,
];

export const RANGE_OPERATORS: readonly FilterOperator[] = [FilterOperator.IsBetween, FilterOperator.IsBetweenEquals];

export const CAP_OPERATORS: readonly FilterOperator[] = [FilterOperator.IsLessThan, FilterOperator.IsLessThanOrEquals];

/** No `equals`: `{time}` is a number of seconds and equals compares as text, so "22:00" never matches it. */
export const TIME_OPERATORS: readonly FilterOperator[] = [
    FilterOperator.IsBetween,
    FilterOperator.IsBetweenEquals,
    FilterOperator.IsLessThan,
    FilterOperator.IsLessThanOrEquals,
    FilterOperator.IsGreaterThan,
    FilterOperator.IsGreaterThanOrEquals,
];

const FIELD_OPERATORS = Object.values(FilterOperator).filter((operator) => operator !== FilterOperator.Always);
const CAP_TAGS = Object.values(CapType) as string[];
const ALLOWED_LEAF_KEYS = ["op", "left", "right", "message"];

export interface ParsedFilter {
    root: FilterEditorRoot;
    /** False when BE filter contains something the editor cannot represent without changing it. */
    supported: boolean;
}

class UnsupportedFilterError extends Error {
    /** The JSON node the editor cannot represent, when known */
    node?: unknown;

    constructor(reason: string, node?: unknown) {
        super(reason);
        this.node = node;
    }
}

const unsupported = (reason: string, node?: unknown): never => {
    throw new UnsupportedFilterError(reason, node);
};

/** Source JSON node of every parsed rule (the first leaf for a merged list), by rule id. */
export type RuleSources = Map<string, unknown>;

// Set only for the duration of `parseFilterTreeDetailed`, so the plain parser pays nothing for it
let activeSources: RuleSources | null = null;

const withSource = <T extends FilterRule>(rule: T, node: unknown): T => {
    activeSources?.set(rule.id, node);
    return rule;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

const extractTag = (value: unknown): string | null => {
    if (typeof value !== "string") {
        return null;
    }
    return /^\{([^{}]+)\}$/.exec(value)?.[1] ?? null;
};

const toTagString = (tag: string): string => `{${tag}}`;

const isScalar = (value: unknown): value is string | number => typeof value === "string" || typeof value === "number";

const isTimeText = (value: unknown): value is string => typeof value === "string" && TIME_PATTERN.test(value);

const isFilterOperator = (value: unknown): value is FilterOperator =>
    typeof value === "string" && (FIELD_OPERATORS as string[]).includes(value);

// ---------------------------------------------------------------------------
// BE tree -> editor rows
// ---------------------------------------------------------------------------

const isLogicalRecord = (node: unknown): node is Record<string, unknown> & { op: FilterLogicalOperator } =>
    isPlainObject(node) && (node.op === "and" || node.op === "or") && "left" in node && "right" in node;

/** Any shape of a chain: `a and (b and c)` and `(a and b) and c` are both `a and b and c`. */
const flattenChain = (node: unknown, op: FilterLogicalOperator): unknown[] => {
    if (isLogicalRecord(node) && node.op === op) {
        return [...flattenChain(node.left, op), ...flattenChain(node.right, op)];
    }
    return [node];
};

/**
 * Rows of the editor are written as a left-deep chain: `a, b, c` => `op(op(a, b), c)`.
 * A node of the same operator on the RIGHT can therefore only be a bracket (group) the user made, so it is kept
 * as one instead of being merged into the chain.
 */
const flattenRows = (node: unknown, op: FilterLogicalOperator): unknown[] => {
    if (isLogicalRecord(node) && node.op === op) {
        return [...flattenRows(node.left, op), node.right];
    }
    return [node];
};

/**
 * "Is one of / is none of" rules. BE has no `in`, so a list is an `or` chain of `equals` (or an `and` chain of
 * `not_equals`) on one tag — and it is read back into a single rule.
 */
interface ListRuleSpec {
    tag: string;
    /** Value of the leaf `right`, or null when it isn't a valid one for this rule */
    parseValue: (right: unknown) => string | number | null;
    create: (mode: "in" | "not_in", values: Array<string | number>) => FilterRule;
}

const LIST_RULES: ListRuleSpec[] = [
    {
        tag: OWNER_ID_TAG,
        parseValue: (right) => (typeof right === "string" && right !== "" ? right : null),
        create: (mode, values) => createAffiliateRule(mode, values as string[]),
    },
    {
        tag: WEEKDAY_TAG,
        parseValue: (right) => {
            const day = typeof right === "string" && /^\d$/.test(right) ? Number(right) : right;
            return typeof day === "number" && WEEKDAYS.includes(day) ? day : null;
        },
        create: (mode, values) => createWeekdayRule(mode, values as number[]),
    },
];

const listRuleOperator = (mode: "in" | "not_in"): FilterOperator =>
    mode === "in" ? FilterOperator.Equals : FilterOperator.NotEquals;

/** The list rule a leaf belongs to for the given operator (equals = "in", not_equals = "not in"), with its value. */
const matchListLeaf = (
    node: unknown,
    operator: FilterOperator
): { spec: ListRuleSpec; value: string | number } | null => {
    if (!isPlainObject(node) || node.op !== operator) {
        return null;
    }
    const tag = extractTag(node.left);
    const spec = LIST_RULES.find((candidate) => candidate.tag === tag);
    const value = spec?.parseValue(node.right) ?? null;
    return spec && value !== null ? { spec, value } : null;
};

/** "A or B or C" on one list tag is one "is one of" rule, "not A and not B" is "is none of". */
const mergeListRule = (node: unknown): FilterRule | null => {
    if (!isLogicalRecord(node)) {
        return null;
    }
    const mode = node.op === "or" ? "in" : "not_in";
    const values: Array<string | number> = [];
    let spec: ListRuleSpec | null = null;
    for (const item of flattenChain(node, node.op)) {
        const match = matchListLeaf(item, listRuleOperator(mode));
        if (!match || (spec && match.spec !== spec)) {
            return null;
        }
        spec = match.spec;
        values.push(match.value);
    }
    return spec ? withSource(spec.create(mode, values), node) : null;
};

const parseLeafRule = (node: Record<string, unknown>): FilterRule => {
    if (Object.keys(node).some((key) => !ALLOWED_LEAF_KEYS.includes(key))) {
        return unsupported("unknown key");
    }
    if (node.message !== undefined && node.message !== null && node.message !== "") {
        return unsupported("custom message");
    }

    const { op, left, right } = node;
    const tag = extractTag(left);
    if (!isFilterOperator(op) || tag === null) {
        return unsupported("unknown operator or tag");
    }

    const listSpec = LIST_RULES.find((candidate) => candidate.tag === tag);
    if (listSpec) {
        const value = listSpec.parseValue(right);
        if (value === null) {
            return unsupported("list rule value");
        }
        if (op === FilterOperator.Equals) {
            return listSpec.create("in", [value]);
        }
        if (op === FilterOperator.NotEquals) {
            return listSpec.create("not_in", [value]);
        }
        return unsupported("list rule operator");
    }

    if (tag === TIME_TAG) {
        if (!TIME_OPERATORS.includes(op)) {
            return unsupported("time operator");
        }
        if (RANGE_OPERATORS.includes(op)) {
            if (!isPlainObject(right) || !isTimeText(right.min) || !isTimeText(right.max)) {
                return unsupported("time range");
            }
            return createTimeRule({ operator: op, value: right.min, valueMax: right.max });
        }
        if (!isTimeText(right)) {
            return unsupported("time value");
        }
        return createTimeRule({ operator: op, value: right });
    }

    if (CAP_TAGS.includes(tag)) {
        if (!CAP_OPERATORS.includes(op) || !isScalar(right)) {
            return unsupported("cap operator or value");
        }
        return createCapRule({ tag: tag as CapType, operator: op, value: String(right) });
    }

    if (tag.startsWith(FORM_FIELD_TAG_PREFIX) && tag.length > FORM_FIELD_TAG_PREFIX.length) {
        if (VALUE_LESS_OPERATORS.includes(op)) {
            return createFieldRule({ tag, operator: op });
        }
        if (RANGE_OPERATORS.includes(op)) {
            if (!isPlainObject(right) || !isScalar(right.min) || !isScalar(right.max)) {
                return unsupported("range value");
            }
            return createFieldRule({ tag, operator: op, value: String(right.min), valueMax: String(right.max) });
        }
        if (!isScalar(right)) {
            return unsupported("field value");
        }
        return createFieldRule({ tag, operator: op, value: String(right) });
    }

    return unsupported("unknown tag");
};

const parseLeaf = (node: Record<string, unknown>): FilterRule => {
    try {
        return withSource(parseLeafRule(node), node);
    } catch (error) {
        if (error instanceof UnsupportedFilterError && error.node === undefined) {
            error.node = node;
        }
        throw error;
    }
};

const parseRule = (node: unknown): FilterRule => {
    if (!isPlainObject(node) || typeof node.op !== "string") {
        return unsupported("not a filter node", node);
    }
    return mergeListRule(node) ?? (isLogicalRecord(node) ? unsupported("nested group", node) : parseLeaf(node));
};

const isRuleObject = (value: unknown): value is FilterRule =>
    isPlainObject(value) && ["affiliate", "field", "cap", "time", "weekday"].includes(value.kind as string);

/**
 * Neighbouring leaves of one list tag in a chain are one rule:
 * `A or B or cap` => [affiliate(A, B), cap], `notA and notB and cap` => [affiliate not(A, B), cap].
 */
const mergeAdjacentListLeaves = (nodes: unknown[], operator: FilterOperator): unknown[] => {
    const result: unknown[] = [];
    let run: { spec: ListRuleSpec; values: Array<string | number>; first: unknown } | null = null;
    const flush = () => {
        if (run) {
            const rule = run.spec.create(operator === FilterOperator.Equals ? "in" : "not_in", run.values);
            result.push(withSource(rule, run.first));
            run = null;
        }
    };
    nodes.forEach((node) => {
        const match = matchListLeaf(node, operator);
        if (match && run?.spec === match.spec) {
            run.values.push(match.value);
        } else if (match) {
            flush();
            run = { spec: match.spec, values: [match.value], first: node };
        } else {
            flush();
            result.push(node);
        }
    });
    flush();
    return result;
};

/** A bracket: rules of one operator. Deeper nesting than one level is not supported by the editor. */
const parseGroup = (node: Record<string, unknown> & { op: FilterLogicalOperator }): FilterGroup =>
    createGroup(
        node.op,
        mergeAdjacentListLeaves(
            flattenChain(node, node.op),
            node.op === "or" ? FilterOperator.Equals : FilterOperator.NotEquals
        ).map((item) => (isRuleObject(item) ? item : parseRule(item)))
    );

const parseItem = (node: unknown): FilterItem => {
    if (!isPlainObject(node) || typeof node.op !== "string") {
        return unsupported("not a filter node", node);
    }
    const merged = mergeListRule(node);
    if (merged) {
        return merged;
    }
    return isLogicalRecord(node) ? parseGroup(node) : parseLeaf(node);
};

/** Items of an AND chain (a run of rows joined by `and`). */
const parseSegment = (node: unknown): FilterItem[] => {
    if (isRuleObject(node)) {
        return [node];
    }
    if (isLogicalRecord(node) && node.op === "and" && !mergeListRule(node)) {
        return mergeAdjacentListLeaves(flattenRows(node, "and"), FilterOperator.NotEquals).map((item) =>
            isRuleObject(item) ? item : parseItem(item)
        );
    }
    return [parseItem(node)];
};

const parseEntries = (tree: unknown): FilterEditorRoot => {
    const segments =
        isLogicalRecord(tree) && tree.op === "or" && !mergeListRule(tree)
            ? mergeAdjacentListLeaves(flattenRows(tree, "or"), FilterOperator.Equals)
            : [tree];

    return segments.flatMap((segment, segmentIndex) => {
        const items = parseSegment(segment);
        return items.map(
            (item, itemIndex): FilterEntry => ({
                item,
                // OR between segments, AND inside one
                join: itemIndex === items.length - 1 && segmentIndex < segments.length - 1 ? "or" : "and",
            })
        );
    });
};

export interface DetailedParsedFilter extends ParsedFilter {
    /** Why and where the filter is unsupported (`node` is the offending JSON node, when known) */
    unsupportedAt?: { reason: string; node: unknown };
    /** Source JSON node of every rule, to point validation issues at their place in the JSON */
    sources: RuleSources;
}

/** Same as `parseFilterTree`, but also says *where* a rule came from and what exactly is unsupported. */
export const parseFilterTreeDetailed = (tree: unknown): DetailedParsedFilter => {
    const sources: RuleSources = new Map();
    if (tree === null || tree === undefined) {
        return { root: [], supported: true, sources };
    }

    activeSources = sources;
    try {
        return { root: parseEntries(tree), supported: true, sources };
    } catch (error) {
        if (error instanceof UnsupportedFilterError) {
            return { root: [], supported: false, sources, unsupportedAt: { reason: error.message, node: error.node } };
        }
        throw error;
    } finally {
        activeSources = null;
    }
};

/**
 * Converts the filter stored on BE (`cascade.filter` / `offer.filter`) to editor rows.
 * Anything the editor cannot represent one-to-one marks the filter as unsupported, so it is never rewritten silently.
 */
export const parseFilterTree = (tree: unknown): ParsedFilter => {
    const { root, supported } = parseFilterTreeDetailed(tree);
    return { root, supported };
};

// ---------------------------------------------------------------------------
// editor rows -> BE tree
// ---------------------------------------------------------------------------

/** BE nodes are binary, editor chains are n-ary. */
const fold = (op: FilterLogicalOperator, nodes: FilterNode[]): FilterNode | null => {
    if (nodes.length === 0) {
        return null;
    }
    return nodes.reduce((left, right) => ({ op, left, right }));
};

const serializeList = (mode: "in" | "not_in", tag: string, values: Array<string | number>): FilterNode | null =>
    fold(
        mode === "in" ? "or" : "and",
        values.map((value) => ({ op: listRuleOperator(mode), left: toTagString(tag), right: value }))
    );

const serializeRule = (rule: FilterRule): FilterNode | null => {
    switch (rule.kind) {
        case "affiliate":
            return serializeList(rule.mode, OWNER_ID_TAG, rule.affiliateIds.filter(Boolean));
        case "weekday":
            return serializeList(rule.mode, WEEKDAY_TAG, rule.days);
        case "time": {
            const left = toTagString(TIME_TAG);
            if (RANGE_OPERATORS.includes(rule.operator)) {
                return { op: rule.operator, left, right: { min: rule.value, max: rule.valueMax } };
            }
            return { op: rule.operator, left, right: rule.value };
        }
        case "cap":
            return { op: rule.operator, left: toTagString(rule.tag), right: Number(rule.value) };
        case "field": {
            const left = toTagString(rule.tag);
            if (VALUE_LESS_OPERATORS.includes(rule.operator)) {
                return { op: rule.operator, left };
            }
            if (RANGE_OPERATORS.includes(rule.operator)) {
                return { op: rule.operator, left, right: { min: rule.value, max: rule.valueMax } };
            }
            return { op: rule.operator, left, right: rule.value };
        }
    }
};

const serializeItem = (item: FilterItem): FilterNode | null => {
    if (item.kind !== "group") {
        return serializeRule(item);
    }
    const rules = item.rules.map(serializeRule).filter((node): node is FilterNode => node !== null);
    return fold(item.operator, rules);
};

/**
 * Empty filter is `null`. Empty groups and unfinished affiliate rules are dropped, single-rule groups are unwrapped.
 * Rows are joined by their own operators, `and` binds stronger than `or`: `A and B or C` => `(A and B) or C`.
 */
export const serializeFilterTree = (root: FilterEditorRoot): FilterNode | null => {
    const rows = root
        .map((entry) => ({ join: entry.join, node: serializeItem(entry.item) }))
        .filter((row): row is { join: FilterLogicalOperator; node: FilterNode } => row.node !== null);

    const segments: FilterNode[][] = [];
    let current: FilterNode[] = [];
    rows.forEach((row, index) => {
        current.push(row.node);
        if (row.join === "or" && index < rows.length - 1) {
            segments.push(current);
            current = [];
        }
    });
    if (current.length > 0) {
        segments.push(current);
    }

    return fold(
        "or",
        segments.map((segment) => fold("and", segment) as FilterNode)
    );
};

const countRawLeaves = (node: unknown): number =>
    isLogicalRecord(node) ? countRawLeaves(node.left) + countRawLeaves(node.right) : isPlainObject(node) ? 1 : 0;

/** Number of rules shown in the editor (used for the badge in the pingtree table). */
export const countFilterRules = (tree: unknown): number => {
    if (tree === null || tree === undefined) {
        return 0;
    }
    const parsed = parseFilterTree(tree);
    return parsed.supported ? countRules(parsed.root) : countRawLeaves(tree);
};
